import PropTypes from 'prop-types'
import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  convertTimeStringWithDuration,
  convertToCurrentGMT,
  getDateRemainFromNow,
} from '_utils/function'
import { getUserToken } from '_utils/localData'
import { formatUnixToDate } from '_utils/formatNumber'
import { IoShareSocialOutline } from 'react-icons/io5'
import { FiLink, FiDownload } from 'react-icons/fi'
import useShareLinkEvent from '_hooks/useShareLinkEvent'
import useUserRole from '_hooks/useUserRole'
import useIsEventClosed from '_hooks/useIsEventClosed'
import ImageSlider from '_components/ImageSlider'
import { useModal } from '_hooks/useModal'
import './style.scss'
import ModalExternalLink from '_pages/Event/components/ModalExternalLink'
import MenuShareTemplate from '_pages/Event/components/MenuShareTemplate'
import useShareTemplate from '_pages/Event/hooks/useShareTemplate'

const getInitials = (value = '') => value
  .split(' ')
  .filter(Boolean)
  .slice(0, 2)
  .map((item) => item[0]?.toUpperCase())
  .join('')

const getDetailLink = (eventId) => {
  if (!getUserToken()) {
    return '/login'
  }

  return `/event/detail?eid=${eventId}`
}

const getCollectionText = (pickupTime, pickupDuration) => {
  if (!pickupTime) return 'TBC'
  if (!pickupDuration) return convertToCurrentGMT(pickupTime)
  try {
    return convertTimeStringWithDuration(pickupTime, pickupDuration)
  } catch {
    return convertToCurrentGMT(pickupTime)
  }
}

const MONTH_NAMES = [
  'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
  'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC',
]

const formatShortDate = (dateStr) => {
  if (!dateStr || dateStr === 'TBC') return dateStr
  const datePart = dateStr.split(' ')[0]
  const parts = datePart.split(/[-/]/)
  if (parts.length >= 2) {
    const day = parts[0]
    const monthIndex = parseInt(parts[1], 10) - 1
    const monthName = MONTH_NAMES[monthIndex]
    if (monthName) {
      return `${day} ${monthName}`
    }
    return `${parts[0]}-${parts[1]}`
  }
  return datePart
}

const EventCard = ({ event, viewMode = 'list', onCardClick, onShareClick, onLinkClick }) => {
  const { isHostOrAdmin } = useUserRole()
  const isTile = viewMode === 'tile' || viewMode === 'grid'

  const avatarText = getInitials(event?.createdUserName || event?.title || 'EV')
  const closingTime = event?.closingTime ? convertToCurrentGMT(event.closingTime) : ''
  const closingDate = closingTime?.split(' ')[0] || 'TBC'

  const closingCountdown = closingTime ? getDateRemainFromNow(closingTime) : '0 day'
  const closingLabel = `Closing in ${closingCountdown}`

  const collectionText = getCollectionText(event?.pickupTime, event?.pickupDuration)
  const totalOrders = Number(event?.totalOrderCount) || 0
  const buyBadgeClassName = totalOrders > 0
    ? 'event-card__buy-badge'
    : 'event-card__buy-badge event-card__buy-badge--empty'
  const openDate = formatUnixToDate(event?.createdAt)

  const displayOpenDate = isTile ? formatShortDate(openDate) : openDate
  const displayClosingDate = isTile ? formatShortDate(closingDate) : closingDate
  const displayCollectionDate = isTile
    ? formatShortDate(event?.pickupTime ? convertToCurrentGMT(event.pickupTime) : collectionText)
    : collectionText

  const isClosed = useIsEventClosed(event)
  const cardClassName = `event-card event-card--${viewMode} ${isClosed ? 'is-closed' : ''}`

  const extraFields = useMemo(() => {
    const rawList = Array.isArray(event?.extraFields)
      ? event.extraFields
      : (Array.isArray(event?.extraFields?.list) ? event.extraFields.list : [])
    return rawList.filter((item) => item && (item.name || item.value))
  }, [event?.extraFields])

  const shouldShowActions = extraFields.length > 0 || isHostOrAdmin || event?.isSharingTemplate

  const [isCopied, setIsCopied] = useState(false)
  const { handleShare, isLoading: isLoadingShare } = useShareLinkEvent()
  const {
    isShareTemplateMenuOpen,
    onClickIconDownload,
    closeMenu,
    handleDownloadExcel,
    handleDownloadPDF,
    handleUseAsTemplate,
  } = useShareTemplate()

  const externalLinkModal = useModal()

  const handleClick = (e) => {
    if (onCardClick) {
      e.preventDefault()
      onCardClick(event, e)
    }
  }

  const onClickShare = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (onShareClick) {
      return onShareClick(event, e)
    }
    if (isLoadingShare || isCopied) return
    handleShare(event?.id, (url) => {
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(url)
      }
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    })
  }

  const handleLinkClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (onLinkClick) {
      return onLinkClick(event, e)
    }
    externalLinkModal.openModal()
  }

  const photoUrl = event?.createdUserPhotoUrl
  const imageList = event?.photoUrls?.length > 0
    ? event.photoUrls
    : (event?.coverPhotoUrl ? [event.coverPhotoUrl] : [])

  return (
    <>
      <Link className={cardClassName} to={getDetailLink(event?.id)} onClick={handleClick}>
        <div className='event-card__header'>
          <div className='event-card__host'>
            <div className='event-card__avatar'>
              {photoUrl ? (
                <img src={photoUrl} alt={event?.createdUserName} />
              ) : (
                avatarText
              )}
            </div>
            <div className='event-card__host-copy'>
              <div className='event-card__event-name'>
                {isTile ? event?.title : event?.groupName}
              </div>
              <div className='event-card__host-sub'>
                Created by {event?.createdUserName || 'Group Host'}
              </div>
            </div>
          </div>
          <div className='event-card__badges'>
            <span className={`event-card__closing-text ${isClosed ? 'closed' : ''}`}>
              {isClosed ? 'Closed' : closingLabel}
            </span>
            <span className={buyBadgeClassName}>{`${totalOrders} BUY`}</span>
          </div>
        </div>
        <div className='event-card__media-wrap'>
          <ImageSlider
            images={imageList}
            alt={event?.title}
            height={isTile ? 165 : undefined}
          />
          {isTile && (
            <span className={`event-card__floating-buy-badge ${buyBadgeClassName}`}>
              {`${totalOrders} BUY`}
            </span>
          )}
        </div>
        {!isTile && event?.title && (
          <div className="event-card__group-name" >
            {event?.title}
          </div>
        )}
        {!isTile && event?.description && (
          <div className='event-card__description'>
            {event?.description}
          </div>
        )}

        <div className={`event-card__info-grid ${!event?.description ? 'no-desc' : ''}`}>
          <span className='event-card__info-label'>Event open:</span>
          <span className='event-card__info-value'>{displayOpenDate}</span>
          <span className='event-card__info-label'>Event close:</span>
          <span className='event-card__info-value'>
            {isTile && !isClosed && (
              <span className='event-card__closing-text' style={{ fontSize: '9px', marginRight: '2px' }}>
                {`(in ${closingCountdown})`}
              </span>
            )}
            {isClosed && <span style={{ color: '#B4B2A9', marginLeft: '4px' }}>(Closed)</span>}
            {displayClosingDate}
          </span>
          <span className='event-card__info-label'>Collection:</span>
          <span className='event-card__info-value'>{displayCollectionDate}</span>
        </div>

        {shouldShowActions && (
          <div className='mef-card-actions'>
            <div className='mef-external-link'>
              {extraFields?.length === 1 && extraFields[0] && (
                <a
                  href={extraFields[0]?.value || '#'}
                  target='_blank'
                  rel='noreferrer'
                  onClick={(e) => {
                    if (onLinkClick) {
                      e.preventDefault()
                      e.stopPropagation()
                      return onLinkClick(event, e)
                    }
                    e.stopPropagation()
                  }}
                  className='mef-external-link__anchor'
                >
                  <FiLink size={10} />
                  <span>{extraFields[0]?.name || extraFields[0]?.value}</span>
                </a>
              )}
              {extraFields?.length > 1 && (
                <span
                  className='mef-external-link__multi'
                  role='button'
                  onClick={handleLinkClick}
                >
                  <FiLink size={10} />
                  <span>{extraFields?.length} Links</span>
                </span>
              )}
            </div>
            <div className='mef-row-actions'>
              {isHostOrAdmin && event?.isSharingTemplate && (
                <div className='mef-share-template-wrapper'>
                  <FiDownload
                    size={15}
                    className='mef-share-template'
                    onClick={(e) => { e.preventDefault(); onClickIconDownload(e); }}
                  />
                  {isShareTemplateMenuOpen && (
                    <MenuShareTemplate
                      isOpen={isShareTemplateMenuOpen}
                      onClose={closeMenu}
                      onDownloadExcel={() => handleDownloadExcel(event?.id)}
                      onDownloadPDF={() => handleDownloadPDF(event?.id)}
                      onUseAsTemplate={() => handleUseAsTemplate(event?.id)}
                    />
                  )}
                </div>
              )}
              {(isHostOrAdmin || event?.isSharingTemplate) && (
                <button
                  type='button'
                  className='mef-share-btn'
                  aria-label='Share event'
                  onClick={onClickShare}
                  disabled={isLoadingShare}
                >
                {isCopied
                  ? <span className='mef-share-btn__copied'>Copied!</span>
                  : <IoShareSocialOutline size={15} />}
                </button>
              )}
            </div>
          </div>
        )}
      </Link>
      {extraFields.length > 1 && (
        <ModalExternalLink
          isOpen={externalLinkModal.isOpen}
          toggle={externalLinkModal.toggleModal}
          extraFields={
            Array.isArray(event?.extraFields)
              ? { list: event.extraFields }
              : event?.extraFields
          }
        />
      )}
    </>
  )
}

EventCard.propTypes = {
  viewMode: PropTypes.string,
  onCardClick: PropTypes.func,
  onShareClick: PropTypes.func,
  onLinkClick: PropTypes.func,
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    description: PropTypes.string,
    closingTime: PropTypes.string,
    pickupTime: PropTypes.string,
    pickupDuration: PropTypes.number,
    pickupAddress: PropTypes.string,
    groupName: PropTypes.string,
    createdUserName: PropTypes.string,
    createdUserPhotoUrl: PropTypes.string,
    photoUrl: PropTypes.string,
    totalOrderCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    photoUrls: PropTypes.arrayOf(PropTypes.string),
    coverPhotoUrl: PropTypes.string,
    createdAt: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    status: PropTypes.string,
    isClosed: PropTypes.bool,
    extraFields: PropTypes.any,
    isSharingTemplate: PropTypes.bool
  }),
}

export default EventCard
